<template>
  <div class="y-current-user" :class="[`y-current-user-` + size]">
    <div class="btns" v-if="!user">
      <y-button
        v-if="
          clients?.web?.registerEnabled && yChannelStore.channel !== 'charlotte'
        "
        class="btn"
        :plain="!type"
        @click="handleRegister"
      >
        {{ t("shared.auth.register") }}
      </y-button>
      <y-button class="btn btn2" :type="type" @click="handleClickLogin">
        {{ t("shared.auth.signIn") }}
      </y-button>
    </div>
    <div v-else class="flex items-center justify-center">
      <el-divider direction="vertical" v-if="showDivider" />
      <div class="hl-Header" v-if="customUser" @click="emit('member')">
        <div class="flex items-center">
          <y-text class="text">
            {{ user.name }}
          </y-text>
          <div class="headImg ml-3">
            <img class="w-10 h-10" src="./icon-head.png" alt="" />
            <img class="h-4 level" :src="memberLevel" alt="" />
          </div>
        </div>
      </div>
      <el-popover
        placement="bottom-end"
        :width="200"
        :disabled="displaySettingsList.length === 0"
        trigger="hover"
        v-else
        v-model:visible="visible"
        popper-class="language-selector-popper"
      >
        <div class="list">
          <div
            v-for="(item, index) in displaySettingsList"
            :key="index"
            class="item"
          >
            <y-text @click="handleClickSetting(item)">
              {{ t(item.label) }}
            </y-text>
            <component
              v-if="item.component"
              :is="item.component.name"
              v-model="item.component.modelValue"
              v-on="item.component.events"
            />
          </div>
        </div>
        <template #reference>
          <div class="flex items-center">
            <y-text class="text">
              {{ user.name }}
            </y-text>
            <img class="w-10 h-10 ml-3" src="./icon-head.png" alt="" />
          </div>
        </template>
      </el-popover>
    </div>
  </div>
</template>

<script setup>
import { get } from "lodash-es";
import { storeToRefs } from "pinia";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";

import { useYChannelStoreHook } from "@/distributions/consumer/y/store/channel";
import { useLoginRedirect } from "@/hooks/useLoginRedirect";
import { useDeploymentStoreHook } from "@/store/modules/deployment";
import { useUserStoreHook } from "@/store/modules/user";

const props = defineProps({
  showRegister: {
    type: Boolean,
    default: true
  },
  // 组件大小
  size: {
    type: String,
    default: "large" // large small
  },
  // 按钮类型
  type: {
    type: String,
    default: "" // primary
  },
  showDivider: {
    type: Boolean,
    default: true
  },
  settings: {
    type: Array,
    default: () => []
  },
  customUser: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits("member");

const { t } = useI18n();
const router = useRouter();
const userStore = useUserStoreHook();
const deploymentStore = useDeploymentStoreHook();
const yChannelStore = useYChannelStoreHook();
const route = useRoute();
const { redirectToLogin } = useLoginRedirect();
const { user } = storeToRefs(userStore);
const { clients } = storeToRefs(deploymentStore);
const visible = ref(false);

const displaySettingsList = computed(() => {
  return props.settings
    .filter(item => {
      if (item.show) {
        return item.show(user.value);
      }
      return true;
    })
    .map(item => {
      if (item.key === "sendWhenBooking") {
        item.component.modelValue = get(
          user.value,
          "featureToggles.confirmationLetter.sendWhenBooking",
          true
        );
      }
      return item;
    });
});

const memberLevel = computed(() => {
  return yChannelStore.configs?.level[
    user.value?.loyaltyProgram?.ref?.type || "guest"
  ];
});

const handleClickLogin = () => {
  redirectToLogin(route.fullPath);
};

const handleRegister = () => {
  const redirectUrl = route.fullPath;
  if (yChannelStore.configs?.registerToSelectPage) {
    router.push({
      path: "/channel-select",
      query: {
        tab: "register",
        redirect: encodeURIComponent(redirectUrl)
      }
    });
  } else {
    router.push({
      path: "/login",
      query: {
        tab: "register",
        redirect: encodeURIComponent(redirectUrl),
        ...route.query
      }
    });
  }
};
const handleClickSetting = item => {
  visible.value = false;
  if (item.path) {
    router.push({
      path: item.path
    });
  }
  if (typeof item.onClick === "function") {
    item.onClick();
  }
};

defineOptions({
  name: "CurrentUser"
});
</script>

<style lang="scss" scoped>
.y-current-user {
  --y-button-height: 42px;
  --y-button-font: Barlow, Barlow;
  --y-button-weight: bold;
  --y-button-size: 16px;
  --y-text-color: #fff;
  --y-text-font-size: 16px;
  --y-text-font-family: var(--y-medium-font);

  .btns {
    display: flex;
  }

  .btn {
    transition: transform 0.3s;
    &:hover {
      transform: scale(1.03);
    }
  }

  &.y-current-user-large {
    .btn {
      padding: 0 36px;
    }

    .btn2 {
      --y-button-border-color: transparent;
      color: var(--y-title-text-color);
    }
  }

  &.y-current-user-small {
    --y-button-height: 40px;
    --y-button-size: 16px;
    --y-text-color: #292929;
    --y-text-font-size: 16px;

    .btn {
      --y-button-space: 12px;
      padding: 0 24px;
    }

    .el-divider {
      height: 40px;
      margin-left: 0;
      margin-right: 20px;
      border-color: #eeeeee;
      opacity: 1;
    }
  }

  .el-divider {
    height: 20px;
    margin-left: 0;
    margin-right: 28px;
    opacity: 0.3;
    border-color: #fff;
  }

  .text {
    align-self: center;
  }
}
.hl-Header {
  cursor: pointer;
  .headImg {
    position: relative;
  }
  .level {
    height: 12px;
    position: absolute;
    bottom: -3px;
    left: 50%;
    transform: translateX(-50%);
  }
}

.list {
  padding: 0 8px;

  .item {
    padding: 20px 0;
    font-size: 16px;
    font-family: Barlow, Barlow;
    font-weight: 500;
    color: var(--y-text-color);
    line-height: 16px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;

    &:not(:last-child) {
      border-bottom: 1px solid #eeeeee;
    }

    &:first-child {
      padding-top: 8px;
    }

    &:last-child {
      padding-bottom: 8px;
    }
  }
}
</style>
